/**
 * `<EditorToolbar>` — the formatting toolbar above the CM6 editor body
 * (UX-reconciliation: matches the standalone's `.ed-tools` row in
 * `docs/refactor/_extracted-standalone-source/04_399994ad.jsx`).
 *
 * Markdown-source-mode commands — these don't fake WYSIWYG, they insert real
 * markdown delimiters that the live preview pane re-renders. Each button
 * dispatches a CM6 transaction against the view passed in via ref.
 *
 *   Bold        — wrap selection with `**…**`
 *   Italic      — wrap selection with `*…*`
 *   Code        — wrap selection with `` `…` ``
 *   Link        — wrap selection with `[…]()`
 *   Heading     — `<select>` prepends `# ` / `## ` / `### ` / nothing
 *   Bulleted    — prepend `- ` to current line
 *   Numbered    — prepend `1. ` to current line
 *   Quote       — prepend `> ` to current line
 *   Hashtag     — insert `#` at cursor (triggers the autocomplete extension)
 *   Wikilink    — insert `[[` at cursor (triggers the wikilink autocomplete)
 *
 * The buttons are no-ops (disabled) until the CM6 view is mounted — surface
 * passes `view = null` while the note query is loading or on a fresh mount.
 */

import { EditorSelection } from '@codemirror/state';
import type { EditorView } from '@codemirror/view';
import { Icon } from '@/components';

interface EditorToolbarProps {
  /** The live CM6 EditorView — `null` while the editor hasn't mounted yet. */
  view: EditorView | null;
}

/**
 * Wrap the current selection (or insert the delimiters at the cursor) with
 * `before…after`. Used by Bold/Italic/Code/Link.
 */
function wrapSelection(
  view: EditorView,
  before: string,
  after: string = before,
): void {
  view.dispatch(
    view.state.changeByRange((range) => {
      const text = view.state.sliceDoc(range.from, range.to);
      return {
        changes: { from: range.from, to: range.to, insert: before + text + after },
        range: EditorSelection.range(
          range.from + before.length,
          range.to + before.length,
        ),
      };
    }),
  );
  view.focus();
}

/**
 * Prepend `prefix` to every line that the current selection touches. Used by
 * Heading / Bulleted / Numbered / Quote — block-level affordances.
 */
function prependToLines(view: EditorView, prefix: string): void {
  view.dispatch(
    view.state.changeByRange((range) => {
      const startLine = view.state.doc.lineAt(range.from);
      const endLine = view.state.doc.lineAt(range.to);
      const changes = [];
      for (let n = startLine.number; n <= endLine.number; n += 1) {
        const line = view.state.doc.line(n);
        changes.push({ from: line.from, insert: prefix });
      }
      const totalInserted = prefix.length * (endLine.number - startLine.number + 1);
      return {
        changes,
        range: EditorSelection.range(
          range.from + prefix.length,
          range.to + totalInserted,
        ),
      };
    }),
  );
  view.focus();
}

/** Insert raw text at the cursor (no selection wrap). Used by Hashtag / Wikilink. */
function insertAtCursor(view: EditorView, text: string): void {
  view.dispatch(
    view.state.changeByRange((range) => ({
      changes: { from: range.from, to: range.to, insert: text },
      range: EditorSelection.cursor(range.from + text.length),
    })),
  );
  view.focus();
}

type HeadingLevel = 'h1' | 'h2' | 'h3' | 'p';

const HEADING_PREFIX: Record<HeadingLevel, string> = {
  h1: '# ',
  h2: '## ',
  h3: '### ',
  p: '',
};

export function EditorToolbar({ view }: EditorToolbarProps): JSX.Element {
  const disabled = view === null;

  const apply = (fn: (v: EditorView) => void): void => {
    if (view === null) return;
    fn(view);
  };

  const onHeading = (e: React.ChangeEvent<HTMLSelectElement>): void => {
    const level = e.target.value as HeadingLevel;
    if (view === null) return;
    // Strip any existing leading `#…` then prepend the new prefix.
    view.dispatch(
      view.state.changeByRange((range) => {
        const startLine = view.state.doc.lineAt(range.from);
        const lineText = startLine.text;
        const stripped = lineText.replace(/^#{1,6}\s+/, '');
        const prefix = HEADING_PREFIX[level];
        const newText = prefix + stripped;
        const delta = newText.length - lineText.length;
        return {
          changes: { from: startLine.from, to: startLine.to, insert: newText },
          range: EditorSelection.cursor(range.from + delta),
        };
      }),
    );
    view.focus();
    // Reset the <select> back to "Heading" so the user can re-apply the same level.
    e.target.value = 'placeholder';
  };

  return (
    <div className="ed-tools" role="toolbar" aria-label="Formatting">
      <div className="ed-tool-group">
        <button
          type="button"
          className="tool"
          title="Bold (⌘B)"
          aria-label="Bold"
          disabled={disabled}
          onClick={() => apply((v) => wrapSelection(v, '**'))}
        >
          <Icon name="bold" size={14} />
        </button>
        <button
          type="button"
          className="tool"
          title="Italic (⌘I)"
          aria-label="Italic"
          disabled={disabled}
          onClick={() => apply((v) => wrapSelection(v, '*'))}
        >
          <Icon name="italic" size={14} />
        </button>
        <button
          type="button"
          className="tool"
          title="Inline code"
          aria-label="Code"
          disabled={disabled}
          onClick={() => apply((v) => wrapSelection(v, '`'))}
        >
          <Icon name="code" size={14} />
        </button>
        <button
          type="button"
          className="tool"
          title="Link"
          aria-label="Link"
          disabled={disabled}
          onClick={() => apply((v) => wrapSelection(v, '[', '](url)'))}
        >
          <Icon name="link" size={14} />
        </button>
      </div>

      <div className="ed-tool-sep" aria-hidden="true" />

      <div className="ed-tool-group">
        <select
          className="ed-tool-heading"
          aria-label="Heading level"
          disabled={disabled}
          defaultValue="placeholder"
          onChange={onHeading}
        >
          <option value="placeholder" disabled hidden>
            Heading
          </option>
          <option value="h1">Heading 1</option>
          <option value="h2">Heading 2</option>
          <option value="h3">Heading 3</option>
          <option value="p">Paragraph</option>
        </select>
        <button
          type="button"
          className="tool"
          title="Bulleted list"
          aria-label="Bulleted list"
          disabled={disabled}
          onClick={() => apply((v) => prependToLines(v, '- '))}
        >
          <Icon name="list" size={14} />
        </button>
        <button
          type="button"
          className="tool"
          title="Numbered list"
          aria-label="Numbered list"
          disabled={disabled}
          onClick={() => apply((v) => prependToLines(v, '1. '))}
        >
          <Icon name="numbered" size={14} />
        </button>
        <button
          type="button"
          className="tool"
          title="Quote"
          aria-label="Quote"
          disabled={disabled}
          onClick={() => apply((v) => prependToLines(v, '> '))}
        >
          <Icon name="quote" size={14} />
        </button>
      </div>

      <div className="ed-tool-sep" aria-hidden="true" />

      <div className="ed-tool-group">
        <button
          type="button"
          className="tool"
          title="Insert hashtag (triggers autocomplete)"
          aria-label="Insert hashtag"
          disabled={disabled}
          onClick={() => apply((v) => insertAtCursor(v, '#'))}
        >
          <Icon name="hashtag" size={14} />
        </button>
        <button
          type="button"
          className="tool"
          title="Insert wikilink (triggers autocomplete)"
          aria-label="Insert wikilink"
          disabled={disabled}
          onClick={() => apply((v) => insertAtCursor(v, '[['))}
        >
          <Icon name="link" size={14} />
        </button>
      </div>
    </div>
  );
}
